import React, { FC, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import './NavbarItem.scss';

interface NavbarItemProps {
  to?: string;
  icon?: React.ReactNode | string | null;
  children: React.ReactNode;
  showArrow?: boolean;
  submenuItems?: { to: string; label: string }[];
  exact?: boolean;
  disabled?: boolean;
  isCollapsed?: boolean;
}

export const NavbarItem: FC<NavbarItemProps> = ({
                                                  to,
                                                  icon,
                                                  children,
                                                  showArrow = false,
                                                  submenuItems = [],
                                                  exact = false,
                                                  disabled = false,
                                                  isCollapsed = false,
                                                }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const isSubmenuActive = submenuItems.some(item => location.pathname === item.to);

  React.useEffect(() => {
    if (isSubmenuActive) {
      setIsOpen(true);
    }
  }, [isSubmenuActive]);

  const handleClick = (e: React.MouseEvent) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    if (submenuItems.length > 0 && !isCollapsed) {
      setIsOpen(!isOpen);
    }
  };

  const isActiveLink = (isActive: boolean, to?: string) => {
    if (disabled || !to) return false;
    if (exact) {
      return location.pathname === to;
    }
    return isActive;
  };

  const linkClassName = `navbar-item__link ${disabled ? 'navbar-item__link--disabled' : ''} ${isCollapsed ? 'navbar-item__link--collapsed' : ''}`;

  const content = (
    <>
      {icon && <div className="navbar-item__link-icon">{icon}</div>}
      {!isCollapsed && (
        <>
          <span className="navbar-item__link-text">{children}</span>
          {showArrow && (
            <div className={`navbar-item__link-arrow ${isOpen ? 'navbar-item__link-arrow--open' : ''}`}>
              <i className="fa-solid fa-chevron-down"></i>
            </div>
          )}
        </>
      )}
    </>
  );

  if (submenuItems.length > 0) {
    const activeSubmenuClass = isSubmenuActive && !disabled ? 'navbar-item__link--active' : '';
    return (
      <li className="navbar-item">
        <div
          className={`${linkClassName} ${activeSubmenuClass}`}
          onClick={handleClick}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
          title={isCollapsed ? children as string : undefined}
        >
          {content}
        </div>
        {isOpen && !disabled && !isCollapsed && (
          <ul className="navbar-item__submenu">
            {submenuItems.map((item, index) => (
              <li key={index} className="navbar-item__submenu-item">
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    isActive
                      ? 'navbar-item__submenu-link navbar-item__submenu-link--active'
                      : 'navbar-item__submenu-link'
                  }
                >
                  • {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  }

  if (disabled) {
    return (
      <li className="navbar-item">
        <div
          className={linkClassName}
          style={{ cursor: 'not-allowed' }}
          title={isCollapsed ? children as string : undefined}
        >
          {content}
        </div>
      </li>
    );
  }

  return (
    <li className="navbar-item">
      <NavLink
        to={to || '#'}
        className={({ isActive }) => {
          const active = isActiveLink(isActive, to);
          return `${linkClassName} ${active ? 'navbar-item__link--active' : ''}`;
        }}
        title={isCollapsed ? children as string : undefined}
      >
        {content}
      </NavLink>
    </li>
  );
};